'use client'

import { createCallable } from 'react-call'

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { cn } from '@/shared/lib/utils'

type ClarifyAction = {
	actionId: string
	ActionProps?: React.ComponentProps<typeof AlertDialogAction>
	actionText?: React.ReactNode
}
interface ClarifyOptions {
	title?: React.ReactNode
	description?: React.ReactNode
	cancelText?: React.ReactNode
	CancelProps?: React.ComponentProps<typeof AlertDialogCancel>
	actions: ClarifyAction[]
}

const defaultOptions = {
	title: 'Are you sure?',
	cancelText: 'Cancel',
	actions: [],
} as const satisfies ClarifyOptions

export type ClarifyResponse = string

const UNMOUNTING_DELAY = 200

const CallableClarify = createCallable<ClarifyOptions, ClarifyResponse>(
	({ call, ...payload }) => {
		const options = { ...defaultOptions, ...payload }

		return (
			<AlertDialog
				open={!call.ended}
				onOpenChange={(open) => !open && call.end('close')}
			>
				<AlertDialogContent className='max-w-md'>
					<AlertDialogHeader>
						<AlertDialogTitle>{options.title}</AlertDialogTitle>
						<AlertDialogDescription>
							{options.description}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel {...options.CancelProps}>
							{options.cancelText}
						</AlertDialogCancel>
						{options.actions.map((action, index) => (
							<AlertDialogAction
								key={action.actionId}
								{...action.ActionProps}
								className={cn(
									index === 0 && 'ml-auto',
									action.ActionProps?.className
								)}
								onClick={() => call.end(action.actionId)}
							>
								{action.actionText}
							</AlertDialogAction>
						))}
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		)
	},
	UNMOUNTING_DELAY
)

const Clarifier = CallableClarify.Root

const clarify = CallableClarify.call

export { Clarifier, clarify }
